import { ArticleLayout } from '@layouts/ArticleLayout';

import * as Typography from '@ui/Typography';

import { CONTACT_INFO } from '@constants/contact';

const displayTitle = 'Complete guide to investing in mutual funds';
const longDescription =
  'Everything you need to know to start your mutual fund investment journey - from basics to advanced strategies, explained in simple terms.';
const readingTime = '15-20 minutes';
const url = `/guides/investing-in-mutual-funds`;
export const Info = {
  displayTitle,
  longDescription,
  readingTime,
  url,
};

export const InvestingInMutualFunds = () => {
  return (
    <ArticleLayout
      title={`${displayTitle} | ${CONTACT_INFO.company.name}`}
      description={longDescription}
      keywords="mutual funds guide, how to invest in mutual funds, SIP investment, mutual fund types, investment guide, financial planning, wealth creation"
      canonicalUrl={`${CONTACT_INFO.social.website}/${url}`}
      heroTitle={displayTitle}
      heroDescription={longDescription}
      breadcrumbItems={[
        { label: 'Home', href: '/' },
        { label: 'Guides', href: '/guides' },
        { label: displayTitle },
      ]}
      sidebar={{
        readingTime,
        author: CONTACT_INFO.contact_person.full_name,
        lastUpdated: new Date('2025-09-25'),
      }}
      cta={{
        title: 'Ready to start your mutual funds journey?',
        description:
          'Get personalized investment advice and start your mutual funds journey with expert guidance.',
        phoneNumber: CONTACT_INFO.phone.whatsapp,
        message: `Hi ${CONTACT_INFO.contact_person.short_name}, I used read your guide on investing in mutual funds. I want to invest in mutual funds. Can you help me get started?`,
        buttonText: 'Get started with mutual funds',
      }}
    >
      {/* Introduction */}
      <Typography.IntroductionBlock>
        <Typography.P className="mb-6">
          Welcome to the world of investing! You've likely heard about mutual
          funds as a great way to grow your money, but the jargon and sheer
          number of options can feel overwhelming. Don't worry. This guide is
          designed to cut through the noise and give you a clear, foundational
          understanding of mutual funds.
        </Typography.P>
        <Typography.P>
          Think of this as your first step towards making your money work for
          you.
        </Typography.P>
      </Typography.IntroductionBlock>

      <Typography.Section>
        <Typography.H2>The basics of mutual funds</Typography.H2>

        <Typography.ContentBlock>
          <Typography.H3>What exactly is a mutual fund?</Typography.H3>
          <Typography.P className="mb-4">
            Imagine you and a group of friends want to order a variety of
            expensive dishes from a restaurant, but individually, you can't
            afford them all. So, you all pool your money together, and with that
            large collective amount, you can buy a bit of everything and share
            the feast.
          </Typography.P>
          <Typography.P className="mb-4">
            A mutual fund works on the exact same principle. It's a
            professionally managed investment vehicle that pools money from many
            investors (like you) and invests it in a diversified portfolio of
            securities like stocks, bonds, government securities, and other
            financial instruments.
          </Typography.P>
          <Typography.P>
            When you invest in a mutual fund, you're essentially buying a small
            portion of a large, professionally managed portfolio. This gives you
            access to investments that might otherwise be out of reach for
            individual investors.
          </Typography.P>
        </Typography.ContentBlock>

        <Typography.ContentBlock>
          <Typography.H3>Key concepts</Typography.H3>
          <Typography.DefinitionList>
            <Typography.DefinitionItem
              term="Asset Management Company (AMC)"
              definition="This is the SEBI-registered company that creates and manages mutual funds. They are responsible for fund operations, compliance, and ensuring the fund meets its stated objectives. Examples in India include HDFC AMC, ICICI Prudential AMC, SBI AMC, Axis AMC, etc."
            />
            <Typography.DefinitionItem
              term="Fund manager"
              definition="A professional expert employed by the AMC who decides which securities to buy or sell, based on the fund's objective."
            />
            <Typography.DefinitionItem
              term="Units"
              definition="When you invest in a mutual fund, you are allotted 'units'. Each unit represents a small slice of ownership in the entire portfolio."
            />
            <Typography.LI>
              <Typography.Strong>Net Asset Value (NAV):</Typography.Strong>
              <Typography.Emphasis>
                {' '}
                This is the price of one unit of a mutual fund. It's calculated
                at the end of every business day. The formula is:
              </Typography.Emphasis>
              <Typography.MathBlock>
                {String.raw`NAV = \frac{\text{Total Assets} - \text{Total Liabilities}}{\text{Total Number of Units Outstanding}}`}
              </Typography.MathBlock>
              <Typography.Emphasis>
                When the value of the securities in the fund's portfolio goes
                up, the NAV increases, and so does the value of your investment.
              </Typography.Emphasis>
            </Typography.LI>
          </Typography.DefinitionList>
        </Typography.ContentBlock>

        <Typography.ContentBlock>
          <Typography.H3>
            Why should a beginner consider mutual funds?
          </Typography.H3>
          <Typography.OL>
            <Typography.LI>
              <Typography.Strong>Professional management:</Typography.Strong>
              <Typography.Emphasis>
                {' '}
                You don't need to be an expert stock-picker. A professional fund
                manager does the research and management for you.
              </Typography.Emphasis>
            </Typography.LI>
            <Typography.LI>
              <Typography.Strong>Diversification:</Typography.Strong>
              <Typography.Emphasis>
                {' '}
                Mutual funds invest in dozens or even hundreds of securities.
                This spreads out your risk. Even if one company's stock performs
                poorly, it's balanced out by the others. It's the classic "don't
                put all your eggs in one basket" strategy, built-in.
              </Typography.Emphasis>
            </Typography.LI>
            <Typography.LI>
              <Typography.Strong>Affordability:</Typography.Strong>
              <Typography.Emphasis>
                {' '}
                You can start investing with as little as ₹500 per month through
                a Systematic Investment Plan (SIP).
              </Typography.Emphasis>
            </Typography.LI>
          </Typography.OL>
        </Typography.ContentBlock>
      </Typography.Section>

      <Typography.Section>
        <Typography.H2>Types of funds</Typography.H2>

        <Typography.P className="mb-8">
          Mutual funds are categorized based on where they invest your money.
          Understanding these categories will help you match a fund to your
          financial goals.
        </Typography.P>

        <Typography.ContentBlock>
          <Typography.H3>1. Based on asset class</Typography.H3>

          <Typography.SubSection>
            <Typography.H4>Equity funds:</Typography.H4>
            <Typography.P>
              These funds primarily invest in the stock market (shares of
              companies). They have the potential for high returns but also come
              with higher risk. They are ideal for long-term goals like
              retirement or wealth creation.
            </Typography.P>
            <Typography.P>
              <Typography.Strong>Examples:</Typography.Strong> Large-cap funds
              (investing in India's top 100 companies), Mid-cap funds (investing
              in medium-sized companies), and Flexi-cap funds (investing across
              companies of all sizes).
            </Typography.P>

            <Typography.H4>Debt funds:</Typography.H4>
            <Typography.P>
              These funds invest in fixed-income instruments like government
              bonds and corporate bonds. They are relatively safer than equity
              funds and are suitable for short-to-medium term goals and capital
              preservation.
            </Typography.P>
            <Typography.P>
              <Typography.Strong>Examples:</Typography.Strong> Liquid funds (for
              very short-term goals), and Corporate Bond funds.
            </Typography.P>

            <Typography.H4>Hybrid funds:</Typography.H4>
            <Typography.P>
              As the name suggests, these funds invest in a mix of both equity
              and debt. They aim to provide a balance between growth and
              stability. They are great for investors who want growth but with
              less risk than pure equity funds.
            </Typography.P>
          </Typography.SubSection>
        </Typography.ContentBlock>

        <Typography.ContentBlock>
          <Typography.H3>2. Based on structure</Typography.H3>
          <Typography.P className="mb-6">
            <Typography.Strong>Open-ended funds:</Typography.Strong>
            <Typography.Emphasis>
              {' '}
              You can buy or sell units of these funds on any business day at
              the current NAV. Most mutual funds in India fall into this
              category. They offer high liquidity and flexibility.
            </Typography.Emphasis>
          </Typography.P>
          <Typography.P className="mb-6">
            <Typography.Strong>Close-ended funds:</Typography.Strong>
            <Typography.Emphasis>
              {' '}
              You can only invest during a specific period (New Fund Offer or
              NFO) and can typically only exit after the fund's tenure is over,
              usually 3-5 years. However, these funds may be listed on stock
              exchanges for trading.
            </Typography.Emphasis>
          </Typography.P>
        </Typography.ContentBlock>

        <Typography.ContentBlock>
          <Typography.H3>3. Based on investment strategy</Typography.H3>
          <Typography.P className="mb-4">
            <Typography.Strong>Actively managed funds:</Typography.Strong>
            <Typography.Emphasis>
              {' '}
              Fund managers actively research and select securities to beat
              market returns. They typically have higher fees but aim for
              superior performance.
            </Typography.Emphasis>
          </Typography.P>
          <Typography.P>
            <Typography.Strong>
              Passively managed funds (Index funds):
            </Typography.Strong>
            <Typography.Emphasis>
              {' '}
              These funds track a specific market index like Nifty 50 or Sensex.
              They have lower fees and aim to match (not beat) market returns.
            </Typography.Emphasis>
          </Typography.P>
        </Typography.ContentBlock>
      </Typography.Section>

      <Typography.Section>
        <Typography.H2>Understanding costs and fees</Typography.H2>

        <Typography.P className="mb-8">
          Every mutual fund charges fees for managing your money. Understanding
          these costs is crucial as they directly impact your returns over time.
        </Typography.P>

        <Typography.ContentBlock>
          <Typography.H3>Key fees you must know</Typography.H3>

          <Typography.DefinitionList>
            <Typography.DefinitionItem
              term="Expense ratio"
              definition="This is the annual fee charged by the fund, expressed as a percentage of your investment. For example, a 1% expense ratio means you pay ₹100 annually for every ₹10,000 invested. SEBI has capped expense ratios at 2.25% for equity funds and 2% for non-equity funds. This usually depends on the assets under management (AUM) of the fund."
            />
            <Typography.DefinitionItem
              term="Exit load"
              definition="A fee charged when you redeem (sell) your units before a specified period. This encourages long-term investing."
            />
          </Typography.DefinitionList>

          <Typography.P className="mt-6">
            <Typography.Strong>Direct vs regular plans:</Typography.Strong>{' '}
            Mutual funds offer two plan options:
          </Typography.P>

          <Typography.UL className="mt-4 text-lg">
            <Typography.LI>
              <Typography.Strong>Direct plans:</Typography.Strong> Lower expense
              ratios (typically 0.5-1% lower) as there's no distributor
              commission. Suitable if you have time and expertise to research,
              select, and monitor funds yourself.
            </Typography.LI>
            <Typography.LI>
              <Typography.Strong>Regular plans:</Typography.Strong> Slightly
              higher expense ratios but come with professional guidance from
              certified distributors. The additional cost can be worthwhile if
              you need help with fund selection, portfolio rebalancing, goal
              planning, and ongoing support.
            </Typography.LI>
          </Typography.UL>

          <Typography.P className="mt-4">
            For beginners or busy professionals who lack time or expertise to
            manage investments, the guidance from a qualified MFD can often
            result in better investment decisions that more than compensate for
            the slightly higher fees.
          </Typography.P>
        </Typography.ContentBlock>
      </Typography.Section>

      <Typography.Section>
        <Typography.H2>Taxation of mutual funds</Typography.H2>

        <Typography.P className="mb-8">
          Understanding how your mutual fund gains are taxed is essential for
          effective financial planning. Tax treatment varies based on the type
          of fund and holding period.
        </Typography.P>

        <Typography.ContentBlock>
          <Typography.H3>Equity funds taxation</Typography.H3>
          <Typography.UL>
            <Typography.LI>
              <Typography.Strong>
                Short-Term Capital Gains (STCG):
              </Typography.Strong>
              <Typography.Emphasis>
                {' '}
                If you sell within 1 year, gains are taxed at 20% + cess.
              </Typography.Emphasis>
            </Typography.LI>
            <Typography.LI>
              <Typography.Strong>
                Long-Term Capital Gains (LTCG):
              </Typography.Strong>
              <Typography.Emphasis>
                {' '}
                If you sell after 1 year, gains up to ₹1.25 lakh per financial
                year are exempted from tax. Only gains exceeding ₹1.25 lakh are
                taxed at 12.5% + cess. This exemption applies to your total
                long-term profits from all equity mutual funds and direct stock
                investments combined.
              </Typography.Emphasis>
            </Typography.LI>
          </Typography.UL>
        </Typography.ContentBlock>

        <Typography.ContentBlock>
          <Typography.H3>Debt funds taxation</Typography.H3>
          <Typography.P>Gains are taxed at applicable slab rates.</Typography.P>
        </Typography.ContentBlock>

        <Typography.ContentBlock>
          <Typography.H3>Tax-saving mutual funds (ELSS)</Typography.H3>
          <Typography.P className="mb-4">
            Equity Linked Savings Scheme (ELSS) funds offer tax deduction up to
            ₹1.5 lakh under Section 80C of Income Tax Act. They have a mandatory
            lock-in period of 3 years, after which LTCG taxation applies.
          </Typography.P>
          <Typography.P>
            <Typography.Strong>Important note:</Typography.Strong> The 3-year
            lock-in period applies to each unit separately based on its purchase
            date. This means if you invest via SIP, each monthly installment
            will have its own 3-year lock-in period starting from that specific
            investment date.
          </Typography.P>
        </Typography.ContentBlock>
      </Typography.Section>

      <Typography.Section>
        <Typography.H2>Understanding risks</Typography.H2>

        <Typography.P className="mb-8">
          While mutual funds offer many benefits, it's important to understand
          the risks involved. All investments carry some level of risk, and
          mutual funds are no exception.
        </Typography.P>

        <Typography.ContentBlock>
          <Typography.H3>Types of risks</Typography.H3>

          <Typography.DefinitionList>
            <Typography.DefinitionItem
              term="Market risk"
              definition="The risk of losing money due to overall market movements. If the stock market falls, equity fund values will also decline. This risk is higher in equity funds and lower in debt funds."
            />
            <Typography.DefinitionItem
              term="Credit risk"
              definition="The risk that the issuer of a bond or security may default on payments. This primarily affects debt funds that invest in corporate bonds with lower credit ratings."
            />
            <Typography.DefinitionItem
              term="Interest rate risk"
              definition="When interest rates change, the value of existing bonds changes inversely. This mainly affects debt funds - when rates rise, bond prices fall."
            />
            <Typography.DefinitionItem
              term="Liquidity risk"
              definition="The risk of not being able to sell your investment quickly at fair value. Most open-ended funds have low liquidity risk, but some specialized funds may face this challenge."
            />
            <Typography.DefinitionItem
              term="Concentration risk"
              definition="Risk arising from investing in too few securities or sectors. Well-diversified funds have lower concentration risk."
            />
          </Typography.DefinitionList>

          <Typography.P className="mt-6">
            So, it's very important to note that higher potential returns
            usually come with higher risks. The key is to choose funds that
            match your risk tolerance and investment horizon.
          </Typography.P>
        </Typography.ContentBlock>
      </Typography.Section>

      <Typography.Section>
        <Typography.H2>How to start investing in mutual funds</Typography.H2>

        <Typography.P className="mb-8">
          Ready to take the plunge? Here's how you can make your first
          investment.
        </Typography.P>

        <Typography.StepList>
          <Typography.StepItem title="Get your KYC done">
            <Typography.P className="mb-4">
              KYC (Know Your Customer) is a mandatory one-time process regulated
              by SEBI. You will need your:
            </Typography.P>
            <Typography.UL spacing="tight" className="text-lg">
              <Typography.LI>Original PAN card</Typography.LI>
              <Typography.LI>Aadhaar Card</Typography.LI>
              <Typography.LI>
                Proof of address (Electricity bill, bank statement, etc.)
              </Typography.LI>
              <Typography.LI>
                A cancelled cheque or bank statement
              </Typography.LI>
            </Typography.UL>
            <Typography.P>
              Most online platforms offer a seamless, paperless e-KYC process
              that takes just a few minutes.
            </Typography.P>
          </Typography.StepItem>

          <Typography.StepItem title="Choose an investment platform">
            <Typography.P className="mb-4">
              You can invest through:
            </Typography.P>
            <Typography.UL>
              <Typography.LI>
                <Typography.Strong>
                  Directly from the AMC's website:
                </Typography.Strong>
                <Typography.Emphasis>
                  {' '}
                  For example, investing in an SBI fund via the SBI Mutual Fund
                  website.
                </Typography.Emphasis>
              </Typography.LI>
              <Typography.LI>
                <Typography.Strong>
                  Online aggregator platforms:
                </Typography.Strong>
                <Typography.Emphasis>
                  {' '}
                  Popular apps like Zerodha Coin, Groww, or Paytm Money allow
                  you to invest in funds from various AMCs all in one place.
                </Typography.Emphasis>
              </Typography.LI>
              <Typography.LI>
                <Typography.Strong>
                  A certified Mutual Fund Distributor (MFD) or financial
                  Advisor:
                </Typography.Strong>
                <Typography.Emphasis>
                  {' '}
                  An advisor can help you select the right funds based on your
                  goals and risk profile.
                </Typography.Emphasis>{' '}
                <Typography.P className="text-sm ml-6 mt-2">
                  *{' '}
                  <Typography.Strong className="text-sm">
                    {CONTACT_INFO.contact_person.full_name}
                  </Typography.Strong>
                  , author of this guide and owner of{' '}
                  <Typography.Link href="/services">
                    {CONTACT_INFO.company.name}
                  </Typography.Link>{' '}
                  is an AMFI certified mutual fund distributor, and he can help
                  you with this. Don't hesitate to{' '}
                  <Typography.Link href="/contact">contact us</Typography.Link>{' '}
                  for a free consultation.
                </Typography.P>
              </Typography.LI>
            </Typography.UL>
          </Typography.StepItem>

          <Typography.StepItem title="Select the right fund">
            <Typography.P className="mb-4">
              This is the most crucial step. Ask yourself three questions:
            </Typography.P>
            <Typography.OL>
              <Typography.LI>
                <Typography.Strong>What is my goal?</Typography.Strong>
                <Typography.Emphasis>
                  {' '}
                  Example: Buying a car in 3 years, child's education in 15
                  years, retirement in 25 years, etc.
                </Typography.Emphasis>
              </Typography.LI>
              <Typography.LI>
                <Typography.Strong>
                  What is my investment horizon?
                </Typography.Strong>
                <Typography.Emphasis>
                  {' '}
                  Example: How long can I stay invested?{' '}
                  <Typography.Emphasis className="text-sm">
                    (For goals over 3-4 years, equity/hybrid funds are suitable.
                    For 1-3 years, consider debt funds.)
                  </Typography.Emphasis>
                </Typography.Emphasis>
              </Typography.LI>
              <Typography.LI>
                <Typography.Strong>
                  What is my risk tolerance?
                </Typography.Strong>
                <Typography.Emphasis>
                  {' '}
                  Example: How comfortable am I with seeing my investment value
                  go up and down?{' '}
                  <Typography.Emphasis className="text-sm">
                    (High risk tolerance suits equity funds, moderate risk suits
                    hybrid funds, low risk suits debt funds.)
                  </Typography.Emphasis>
                </Typography.Emphasis>
              </Typography.LI>
            </Typography.OL>
            <Typography.P>
              You can use our free{' '}
              <Typography.Link href="/tools/goal-planner">
                Goal planner
              </Typography.Link>{' '}
              to articulate your goals and investment horizon.
            </Typography.P>

            <Typography.P className="mb-4 mt-6">
              <Typography.Strong>Fund selection criteria:</Typography.Strong>
            </Typography.P>
            <Typography.UL>
              <Typography.LI>
                <Typography.Strong>Performance consistency:</Typography.Strong>
                <Typography.Emphasis>
                  {' '}
                  Look for funds that have consistently performed well across
                  different market cycles, not just recent winners.
                </Typography.Emphasis>
              </Typography.LI>
              <Typography.LI>
                <Typography.Strong>Fund size (AUM):</Typography.Strong>
                <Typography.Emphasis>
                  {' '}
                  Choose funds with at least ₹1000 crores AUM. Very small funds
                  may face liquidity issues, while very large funds (₹50,000+
                  crores AUM) may find it difficult to generate alpha.
                </Typography.Emphasis>
              </Typography.LI>
              <Typography.LI>
                <Typography.Strong>
                  Fund manager track record:
                </Typography.Strong>
                <Typography.Emphasis>
                  {' '}
                  Check how long the current fund manager has been managing the
                  fund and their overall experience.
                </Typography.Emphasis>
              </Typography.LI>
              <Typography.LI>
                <Typography.Strong>Expense ratio:</Typography.Strong>
                <Typography.Emphasis>
                  {' '}
                  Lower expenses mean higher returns for you. Compare similar
                  funds and choose ones with reasonable expense ratios.
                </Typography.Emphasis>
              </Typography.LI>
            </Typography.UL>

            <Typography.P>
              These are just some of the criteria to consider and it may not
              work for all funds, so it's important to do your own research or
              consult a financial advisor if you are not sure.
            </Typography.P>

            <Typography.P className="mt-6">
              If you are a beginner, start with Index Funds (like Nifty 50 Index
              Fund) or Large-cap funds for equity exposure, and Liquid funds or
              Ultra Short Duration funds for debt exposure. These are simple,
              well-diversified, and have lower risks within their categories.
            </Typography.P>
          </Typography.StepItem>

          <Typography.StepItem title="Choose your investment mode: SIP or Lumpsum">
            <Typography.SubSection>
              <div className="mb-6">
                <Typography.H5 className="mb-2">
                  <Typography.Link href="/tools/sip-calculator">
                    SIP (Systematic Investment Plan):
                  </Typography.Link>
                </Typography.H5>
                <Typography.P className="mb-2">
                  You invest a fixed amount every month (or weekly, quarterly,
                  etc.) on a specific date. This is highly recommended for
                  beginners as it builds discipline, averages out your purchase
                  cost over time (rupee cost averaging), and reduces timing
                  risk.
                </Typography.P>
              </div>
              <div className="mb-6">
                <Typography.H5 className="mb-2">
                  <Typography.Link href="/tools/lumpsum-calculator">
                    Lumpsum:
                  </Typography.Link>
                </Typography.H5>
                <Typography.P className="mb-2">
                  You invest a single, large amount at one go. This works well
                  when you have surplus money and markets are at reasonable
                  valuations. This gives you immediate full exposure to markets,
                  potentially higher returns if invested at the right time, no
                  recurring transaction charges.
                </Typography.P>
              </div>
              <Typography.P>
                <Typography.Strong>Which to choose?</Typography.Strong> For
                regular income earners, SIP is usually better. If you have a
                windfall (bonus, inheritance, etc.), consider a combination.
                Invest 50-70% as lumpsum and the rest via SIP over 6-12 months.
              </Typography.P>
            </Typography.SubSection>
          </Typography.StepItem>

          <Typography.StepItem title="Invest and monitor">
            <Typography.P className="mb-4">
              Once you've made your decision, complete the payment.
              Congratulations, you are now a mutual fund investor!
            </Typography.P>
            <Typography.P>
              Remember, investing is a long-term game. You should not be worried
              about daily market noise. Avoid reacting to daily fluctuations.
              Review your portfolio quarterly. Check if your funds are meeting
              their objectives and if your asset allocation is still aligned
              with your goals. Rebalance quarterly if needed. Take help of a
              financial advisor if you are not sure.
            </Typography.P>
          </Typography.StepItem>
        </Typography.StepList>
      </Typography.Section>

      <Typography.Section>
        <Typography.H2>How to evaluate fund performance</Typography.H2>

        <Typography.P className="mb-8">
          Once you start investing, it's important to know how to evaluate
          whether your funds are performing well. Here are key metrics to
          understand:
        </Typography.P>

        <Typography.ContentBlock>
          <Typography.H3>Key performance metrics</Typography.H3>

          <Typography.DefinitionList>
            <Typography.DefinitionItem
              term="Returns"
              definition="Look at returns over multiple time periods, not just recent performance. Pay attention to 3-year and 5-year returns as they show consistency across market cycles."
            />
            <Typography.DefinitionItem
              term="Benchmark comparison"
              definition="Compare your fund's performance with its benchmark index. For example, a Large-cap fund should be compared with Nifty 100 or Sensex. Consistent outperformance over 3-5 years is a good sign."
            />
            <Typography.DefinitionItem
              term="Category average"
              definition="See how your fund performs compared to other similar funds. If your fund is consistently among the top 25% performers in its category over 3-5 years, it shows good management."
            />
            <Typography.DefinitionItem
              term="Sharpe ratio"
              definition="This measures risk-adjusted returns. A higher Sharpe ratio means better returns per unit of risk taken. Look for funds with consistently higher Sharpe ratios."
            />
            <Typography.DefinitionItem
              term="Standard deviation"
              definition="This measures volatility. Lower standard deviation means less fluctuation in returns. Choose based on your risk tolerance."
            />
          </Typography.DefinitionList>

          <Typography.P className="mt-6">
            Don't make investment decisions based on just one metric. Look at
            multiple factors including consistency, risk-adjusted returns, and
            fund manager stability.
          </Typography.P>
        </Typography.ContentBlock>
      </Typography.Section>

      <Typography.Section>
        <Typography.H2>Common mistakes to avoid</Typography.H2>

        <Typography.P className="mb-8">
          Even experienced investors make these mistakes. Being aware of them
          can save you significant losses and improve your investment outcomes.
        </Typography.P>

        <Typography.ContentBlock>
          <Typography.UL>
            <Typography.LI>
              <Typography.Emphasis>
                Don't invest in funds just because they were last year's top
                performers. Past performance doesn't guarantee future results.
                Focus on consistency over multiple market cycles.
              </Typography.Emphasis>
            </Typography.LI>
            <Typography.LI>
              <Typography.Emphasis>
                Nobody can consistently predict market tops and bottoms. Stay
                invested through SIPs regardless of market conditions. Time in
                the market beats timing the market.
              </Typography.Emphasis>
            </Typography.LI>
            <Typography.LI>
              <Typography.Emphasis>
                Having a large number of mutual funds doesn't add value and
                makes portfolio tracking difficult. Well-chosen 5-8 funds across
                categories are sufficient for most investors.
              </Typography.Emphasis>
            </Typography.LI>
            <Typography.LI>
              <Typography.Emphasis>
                Don't put all money in equity funds just because they give high
                returns. Maintain proper asset allocation based on your age,
                goals, and risk tolerance.
              </Typography.Emphasis>
            </Typography.LI>
            <Typography.LI>
              <Typography.Emphasis>
                Constantly switching between funds based on short-term
                performance can hurt returns due to exit loads and timing
                issues. Give funds at least 3-5 years to perform.
              </Typography.Emphasis>
            </Typography.LI>
            <Typography.LI>
              <Typography.Emphasis>
                Each investment should have a specific goal and time horizon.
                This helps you choose the right fund category and stay committed
                during market volatility.
              </Typography.Emphasis>
            </Typography.LI>
            <Typography.LI>
              <Typography.Emphasis>
                Market corrections are normal and temporary. Selling during
                falls locks in losses and prevents you from participating in the
                recovery. Stay disciplined and continue your SIPs.
              </Typography.Emphasis>
            </Typography.LI>
          </Typography.UL>
        </Typography.ContentBlock>
      </Typography.Section>

      <Typography.Section>
        <Typography.H2>Sample portfolio for beginners</Typography.H2>

        <Typography.P className="mb-8">
          Here's a simple, diversified portfolio structure that beginners can
          consider. Adjust the allocation based on your age, goals, and risk
          tolerance.
        </Typography.P>

        <Typography.ContentBlock>
          <Typography.H3>Conservative portfolio (Age 50+)</Typography.H3>
          <Typography.UL className="text-lg">
            <Typography.LI>
              40% Large-cap Equity Fund or Nifty 50 Index Fund
            </Typography.LI>
            <Typography.LI>20% Balanced Advantage Fund</Typography.LI>
            <Typography.LI>
              30% Corporate Bond Fund or Banking & PSU Debt Fund
            </Typography.LI>
            <Typography.LI>10% Liquid Fund (for emergency needs)</Typography.LI>
          </Typography.UL>
        </Typography.ContentBlock>

        <Typography.ContentBlock>
          <Typography.H3>Moderate portfolio (Age 30-50)</Typography.H3>
          <Typography.UL className="text-lg">
            <Typography.LI>40% Large-cap Equity Fund</Typography.LI>
            <Typography.LI>25% Mid-cap or Flexi-cap Fund</Typography.LI>
            <Typography.LI>20% Hybrid Fund</Typography.LI>
            <Typography.LI>10% International Fund</Typography.LI>
            <Typography.LI>
              5% ELSS Fund (for tax saving if you have opted for the old tax
              regime)
            </Typography.LI>
          </Typography.UL>
        </Typography.ContentBlock>

        <Typography.ContentBlock>
          <Typography.H3>Aggressive portfolio (Age below 30)</Typography.H3>
          <Typography.UL className="text-lg">
            <Typography.LI>
              50% Large-cap Equity Fund or Index Fund
            </Typography.LI>
            <Typography.LI>30% Mid-cap Fund</Typography.LI>
            <Typography.LI>15% Small-cap Fund</Typography.LI>
            <Typography.LI>
              5% ELSS Fund (for tax saving if you have opted for the old tax
              regime)
            </Typography.LI>
          </Typography.UL>
        </Typography.ContentBlock>

        <Typography.P className="mt-6">
          Note that these are just sample allocations. Your actual portfolio
          should be customized based on your specific financial situation,
          goals, and risk tolerance. Consider consulting a financial advisor for
          personalized advice.
        </Typography.P>
      </Typography.Section>

      <Typography.Section>
        <Typography.H2>Final thoughts</Typography.H2>
        <Typography.P className="mb-4">
          Mutual fund investing is one of the most accessible and effective ways
          to build wealth over the long term. The key principles are simple:
          start early, invest regularly, stay disciplined, and let the power of
          compounding work its magic.
        </Typography.P>
        <Typography.P className="mb-4">
          Remember that investing is a marathon, not a sprint. Market volatility
          is normal, and short-term fluctuations should not derail your
          long-term investment strategy. Focus on your goals, maintain proper
          asset allocation, and review your portfolio periodically but not
          obsessively.
        </Typography.P>
        <Typography.P>
          Most importantly, don't let analysis paralysis prevent you from
          starting. The best time to start investing was yesterday; the
          second-best time is today. Even small amounts invested consistently
          can grow into substantial wealth over time. Check out our free{' '}
          <Typography.Link href="/tools/sip-calculator">
            SIP calculator
          </Typography.Link>{' '}
          to see how your money grows over time, or use our{' '}
          <Typography.Link href="/tools/swp-calculator">
            SWP calculator
          </Typography.Link>{' '}
          to plan your retirement withdrawals.
        </Typography.P>
      </Typography.Section>
    </ArticleLayout>
  );
};
